import { Button, TextField, Typography, Grid } from '@material-ui/core';
import AddOutlinedIcon from '@material-ui/icons/AddOutlined';
import { Formik, FormikProps } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { IProductTag } from '../../../models/ProductTag.model';
import ProductTagService from '../../../services/ProductTag/ProductTag.service';
import './ProductTags.scss';

interface IAddProductTagFormValues {
  name: string;
}

interface IProductTagsProps {
  venueId: string;
}

const ProductTags = ({ venueId }: IProductTagsProps): JSX.Element => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [tags, setTags] = useState<IProductTag[]>([]);

  const loadTags = async () => {
    setLoading(true);
    try {
      const { data } = await ProductTagService.getAllProductTag(venueId);
      setTags(data);
    } catch (error) {
      console.log(error.response);
      enqueueSnackbar('Something went wrong while loading tags.', {
        variant: 'error',
        autoHideDuration: 8000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTags();
  }, [venueId]);

  const handleAddTag = async (
    values: IAddProductTagFormValues,
    formikHelpers: any,
  ) => {
    const { name } = values;
    const { resetForm, setSubmitting } = formikHelpers;

    try {
      if (name.trim().length <= 0) {
        return;
      }

      await ProductTagService.createProductTag({
        name: name.trim(),
        venueId,
      });

      await loadTags();
      resetForm();
      enqueueSnackbar(`Added tag: ${name}`, { variant: 'success' });
    } catch (error) {
      console.log(error.response);
      const message = error.response?.data?.message || 'Error adding tag';
      enqueueSnackbar(`Error: ${message}`, {
        variant: 'error',
        autoHideDuration: 8000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTag = async (tagId: string) => {
    setLoading(true);
    try {
      await ProductTagService.deleteProductTag(tagId);
      await loadTags();
      enqueueSnackbar('Tag deleted successfully', { variant: 'success' });
    } catch (error) {
      console.log(error.response);
      enqueueSnackbar('Error deleting tag', {
        variant: 'error',
        autoHideDuration: 8000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="product-tags">
      <Typography component="h1" variant="h5" className="section-title">
        Product Tags
      </Typography>

      <Formik<IAddProductTagFormValues>
        initialValues={{
          name: '',
        }}
        onSubmit={handleAddTag}
        validationSchema={Yup.object().shape({
          name: Yup.string()
            .min(1)
            .required('Tag name is required'),
        })}
      >
        {(formProps: FormikProps<IAddProductTagFormValues>) => (
          <div className="product-tags__form">
            <TextField
              error={formProps.touched.name && !!formProps.errors.name}
              required={true}
              name="name"
              label="New Tag Name"
              onChange={formProps.handleChange}
              onBlur={formProps.handleBlur}
              value={formProps.values.name}
              autoComplete="off"
              className="product-tags__input"
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  formProps.submitForm();
                }
              }}
            />

            <Button
              size="medium"
              color="primary"
              variant="contained"
              className="product-tags__add-button"
              onClick={() => formProps.submitForm()}
              disabled={loading || formProps.isSubmitting || !formProps.isValid}
            >
              <div className="icon-title-wrapper">
                <AddOutlinedIcon />
                <span className="icon-title">Add Tag</span>
              </div>
            </Button>
          </div>
        )}
      </Formik>

      <div className="product-tags__container">
        {tags.length === 0 && !loading && (
          <Typography variant="body1">No tags found for this venue.</Typography>
        )}
        <Grid container spacing={2}>
          {tags.map((tag: IProductTag) => (
            <Grid item key={tag.id}>
              <div className="product-tags__tag-chip">
                <span>{tag.name}</span>
                <Button
                  size="small"
                  className="product-tags__delete-btn"
                  onClick={() => handleDeleteTag(tag.id)}
                  disabled={loading}
                >
                  &times;
                </Button>
              </div>
            </Grid>
          ))}
        </Grid>
      </div>
    </div>
  );
};

export default ProductTags;
